// #460 · UNIDAD ACEPTABLE: la identidad de lo que el Aceptar crea como UN alta.
//
// QUÉ HACE ESTE FICHERO, DESPUÉS DE LA DECISIÓN DEL 16/10/2026 (opción A). La regla que convierte
// bloques del solver en altas —y que depende del tipo de trabajo— la aplica AHORA LA API, sobre la
// propuesta que la corrida tiene guardada, y el plan de unidades que produce es el único denominador
// que se cuenta. Aquí quedan dos cosas, y ninguna de las dos es un recuento:
//
//   1. las CLAVES con las que la consolidación del Aceptar agrupa los bloques que va a crear
//      (`dedupePropuestas`): la pantalla sigue siendo la que decide qué altas manda, y esta tarea no
//      cambia esa decisión ni una coma;
//   2. `claveBloque`, que es la clave de JOIN entre las filas de `resultado.propuestasResumen` y los
//      bloques del plan que devuelve la API. No es una regla: son los mismos campos del mismo bloque
//      a los dos lados.
//
// LO QUE YA NO ESTÁ, Y ES EL FONDO DEL CAMBIO: construir un plan y declararlo. Ni esta pantalla ni
// el informe ni el panel deciden cuántas unidades tenía una corrida ni cuántas se aceptaron: eso lo
// dice la API y aquí se lee (ver `src/js/RecuentoPropuesta.js`). Un denominador que anuncia quien
// llama no se puede comprobar del todo, y por ahí volvía el mismo defecto ronda tras ronda.
//
// Las identidades siguen teniendo la MISMA forma que las de la API (`UnidadAceptable.java`) porque
// el Aceptar manda la suya en `unidadId` y el registro avisa en el log si las dos reglas se separan.
// Ese aviso es para lo que sirve: no acredita nada.
//
// VERSIONADO: `VERSION` es la de las reglas de identidad de la API, y es la que `RecuentoPropuesta`
// exige al plan guardado. Si cambiara, los planes antiguos siguen significando lo que significaban.

/** Versión de las reglas de identidad. La misma que `UnidadAceptable.VERSION` de la API. */
const VERSION = 2;

const TIPO_CAMPO = 'CAMPO';
const TIPO_INFORME = 'INFORME';
const TIPO_CONTINUACION = 'CONTINUACION';

function p2(n) { return (n < 10 ? '0' : '') + n; }

function fecha(v) {
    if (!v) return null;
    const f = (v instanceof Date) ? v : new Date(v);
    return isNaN(f.getTime()) ? null : f;
}

/**
 * Día de negocio del bloque, 'YYYY-MM-DD', o '?' si no trae hora —el mismo valor que daba
 * `moment(horaInicio).format('YYYY-MM-DD')`, que es lo que usaban las claves del de-dupe—. El '?'
 * ordena después de cualquier fecha, que es lo que hace que una visita sin hora no se lleve el día
 * de creación de una que sí la tiene.
 */
function claveDia(pl) {
    const f = fecha(pl && pl.horaInicio);
    if (!f) return '?';
    return f.getFullYear() + '-' + p2(f.getMonth() + 1) + '-' + p2(f.getDate());
}

/**
 * El tipo que la corrida tiene GUARDADO para este bloque (`propuestasResumen.tipoUnidad`), si el
 * bloque viene de ahí. Manda sobre la deducción de abajo, y por eso se mira primero: la API lo
 * clasificó UNA vez, con el bloque entero del solver delante, y es el mismo dato con el que deriva
 * el plan de la corrida.
 *
 * Hace falta porque la propuesta reconstruida del registro (cuando `/resultado` ya ha caducado) no
 * puede traer el `tipoTarea`: el audit-log guarda `tipo`, y la reconstrucción aproximaba
 * «TAREA → INFORME». Con esa aproximación una continuación multidía se identificaba como informe
 * —una unidad por `lineaId`, sin día—, así que descartar un día se llevaba todos los días de la
 * visita y el registro los anotaba todos como descartados (P2 de Codex del 16/10/2026).
 */
function tipoGuardado(pl) {
    const t = pl && pl.tipoUnidad;
    return (t === TIPO_CAMPO || t === TIPO_INFORME || t === TIPO_CONTINUACION) ? t : null;
}

/** Continuación multidía: los días 2..N de una visita de campo, como tarea T0004. */
function esContinuacion(pl) {
    const guardado = tipoGuardado(pl);
    if (guardado) return guardado === TIPO_CONTINUACION;
    return !!(pl && pl.tipo === 'TAREA' && pl.tipoTarea && pl.tipoTarea.codigo === 'T0004');
}

/** Producción de informes (TPI): ocupa jornada, no es una visita de campo. */
function esInforme(pl) {
    const guardado = tipoGuardado(pl);
    if (guardado) return guardado === TIPO_INFORME;
    return !!(pl && pl.tipoTrabajo === 'INFORME');
}

function tipo(pl) {
    if (esContinuacion(pl)) return TIPO_CONTINUACION;
    if (esInforme(pl)) return TIPO_INFORME;
    return TIPO_CAMPO;
}

/** (pedido, línea) de un bloque. `numeroPedido` primero: `codigoPedido` del campo es el de LÍNEA. */
function refPedidoLinea(pl) {
    return String((pl && (pl.numeroPedido || pl.codigoPedido)) || '') + '#' + (pl ? pl.numLinea : '');
}

/**
 * La clave con la que la consolidación AGRUPA los bloques que va a crear, por tipo de trabajo. Es la
 * clave del de-dupe, literalmente: `dedupePropuestas` llama aquí y no mantiene su propia copia.
 */
function clave(pl) {
    if (esContinuacion(pl)) return 'T0004:' + refPedidoLinea(pl) + '@' + claveDia(pl);
    if (esInforme(pl)) return 'TPIP:' + (pl && pl.lineaId);
    return 'CAMPO:' + refPedidoLinea(pl);
}

/**
 * ¿Los datos de este bloque determinan a qué unidad pertenece? Misma regla que el `idDe` de la API,
 * que devuelve `null` cuando no: un informe sin `lineaId`, o una línea sin pedido o sin número de
 * línea, no se pueden situar en ninguna unidad. Hace falta decirlo porque esos bloques comparten una
 * clave DEGENERADA ('CAMPO:#'), así que agrupar por ella metería en el mismo saco trabajos distintos.
 */
function identificable(pl) {
    if (!pl) return false;
    if (esInforme(pl)) return !!pl.lineaId;
    const pedido = pl.numeroPedido || pl.codigoPedido;
    return pedido != null && pedido !== '' && pl.numLinea != null;
}

/**
 * Identidad versionada de la unidad aceptable a la que pertenece un bloque. Viaja en el alta
 * (`unidadId`) para que el registro pueda avisar si esta regla y la de la API se separan; el que
 * cuenta es el que la API deriva del alta ya creada.
 */
function id(pl) {
    return 'u' + VERSION + ':' + clave(pl);
}

/**
 * Identidad de un BLOQUE del solver (no de la unidad), al minuto. Es la clave de JOIN entre las
 * filas de `resultado.propuestasResumen` y los bloques de origen del plan que guarda la API: las dos
 * caras salen del mismo bloque del solver, así que traen los mismos valores en estos campos. Mismo
 * formato que `DesviacionDesplazamiento.claveBloque` de la API.
 */
function claveBloque(b) {
    const f = fecha(b && b.horaInicio);
    const momento = f ? Math.floor(f.getTime() / 60000) : '?';
    return (b && b.lineaId ? b.lineaId : '') + '|'
        + (b && b.numeroPedido != null ? b.numeroPedido : null) + '#'
        + (b && b.numLinea != null ? b.numLinea : null) + '@' + momento;
}

/** La referencia de un bloque tal como la espera el descarte de la API (sin identidad de unidad). */
function refBloque(pl) {
    return {
        lineaId: (pl && pl.lineaId) || null,
        numeroPedido: pl && pl.numeroPedido != null ? pl.numeroPedido : null,
        numLinea: pl && pl.numLinea != null ? pl.numLinea : null,
        horaInicio: (pl && pl.horaInicio) || null
    };
}

export default {
    VERSION, TIPO_CAMPO, TIPO_INFORME, TIPO_CONTINUACION,
    claveDia, esContinuacion, esInforme, identificable, tipo, clave, id, claveBloque, refBloque
};
export {
    VERSION, TIPO_CAMPO, TIPO_INFORME, TIPO_CONTINUACION,
    claveDia, esContinuacion, esInforme, identificable, tipo, clave, id, claveBloque, refBloque
};
